import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import type { ClientResponse } from '@clarity/contract';
import { useSession } from './session.js';
import { useClients } from '../queries/index.js';

/**
 * The selected-client context — now DERIVED FROM THE URL (`/c/:clientId/...`),
 * not React state, so every workspace has a real address and the browser back/
 * forward stack and deep links all work.
 *
 * - An admin's top level (`/clients`, `/brands`) has no `:clientId`; to act on a
 *   client they navigate into `/c/:clientId/...`, which puts the shell in that
 *   client's context with the same nav the client sees.
 * - A client-role user is always inside their own context; routes still carry
 *   their `:clientId` so the URLs are stable and shareable within their tenant.
 *
 * This stays a UI scoping convenience over the typed query client — the server
 * enforces tenant isolation regardless.
 */
export interface ClientContextValue {
  /** The chosen client (looked up from the clients list by the URL param), or null. */
  selectedClient: ClientResponse | null;
  /** Whether the shell is currently inside a client workspace. */
  inClientContext: boolean;
  /** The clientId the per-client screens scope to (from the route). */
  activeClientId: string | null;
  /** Enter a client's workspace (navigates to its dashboard). */
  enterClient: (client: ClientResponse) => void;
  /** Leave the client workspace back to the admin top level. */
  exitClient: () => void;
}

const ClientContextCtx = createContext<ClientContextValue | null>(null);

export function ClientContextProvider({ children }: { children: ReactNode }) {
  const { role, clientId: ownClientId } = useSession();
  // Read the `:clientId` from the URL via a path match, so this works even though
  // the provider sits ABOVE the <Route> that declares the param (useParams would
  // see nothing here). Matches `/c/:clientId` and anything nested under it.
  const match = useMatch('/c/:clientId/*');
  const routeClientId = match?.params.clientId;
  const navigate = useNavigate();
  const isAdmin = role === 'admin';

  // Resolve the full client record from the cached clients list when we have one
  // (admins). A client-role user may not list all clients, so fall back to a
  // minimal record built from the session so the header still names the tenant.
  const clients = useClients();
  const clientRows: ClientResponse[] = clients.data?.status === 200 ? clients.data.body : [];

  const value = useMemo<ClientContextValue>(() => {
    const activeClientId = routeClientId ?? (isAdmin ? null : ownClientId);
    const selectedClient =
      activeClientId !== null && activeClientId !== undefined
        ? (clientRows.find((c) => c.id === activeClientId) ?? null)
        : null;

    return {
      selectedClient,
      inClientContext: activeClientId !== null && activeClientId !== undefined,
      activeClientId: activeClientId ?? null,
      enterClient: (client) => navigate(`/c/${client.id}/dashboard`),
      exitClient: () => navigate('/clients'),
    };
  }, [routeClientId, isAdmin, ownClientId, clientRows, navigate]);

  return <ClientContextCtx.Provider value={value}>{children}</ClientContextCtx.Provider>;
}

export function useClientContext(): ClientContextValue {
  const ctx = useContext(ClientContextCtx);
  if (!ctx) {
    throw new Error('useClientContext must be used within a ClientContextProvider');
  }
  return ctx;
}
